const moment = require('moment-timezone');

const TIMEZONE = 'Asia/Kolkata';

/**
 * Get current date in India timezone (YYYY-MM-DD format)
 */
export const getCurrentIndiaDate = (): string => {
  return moment.tz(TIMEZONE).format('YYYY-MM-DD');
};

/**
 * Get current month in India timezone (YYYY-MM format)
 */
export const getCurrentIndiaMonth = (): string => {
  return moment.tz(TIMEZONE).format('YYYY-MM');
};

/**
 * Get current timestamp
 */
export const getCurrentTimestamp = (): Date => {
  return new Date();
};

/**
 * Format date for display (DD MMM YYYY)
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return moment.tz(date, TIMEZONE).format('DD MMM YYYY');
};

/**
 * Format datetime for display (DD MMM YYYY, h:mm A)
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return moment.tz(date, TIMEZONE).format('DD MMM YYYY, h:mm A');
};

/**
 * Format time only (h:mm A)
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return moment.tz(date, TIMEZONE).format('h:mm A');
};

/**
 * Get date string for API calls (YYYY-MM-DD)
 */
export const getApiDateString = (date: Date | string): string => {
  return moment.tz(date, TIMEZONE).format('YYYY-MM-DD');
};

/**
 * Parse date string to Date object
 */
export const parseDate = (dateStr: string): Date => {
  return moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE).toDate();
};

/**
 * Get start and end of month for given month/year
 */
export const getMonthDateRange = (year: number, month: number): { startDate: string; endDate: string } => {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = moment(startDate).endOf('month').format('YYYY-MM-DD');
  return { startDate, endDate };
};

/**
 * Get relative date display (Today, Yesterday, etc.)
 */
export const getRelativeDate = (date: string | Date): string => {
  const dateMoment = moment.tz(date, TIMEZONE);
  const today = moment.tz(TIMEZONE).startOf('day');
  const yesterday = moment.tz(TIMEZONE).subtract(1, 'day').startOf('day');
  
  if (dateMoment.isSame(today, 'day')) {
    return 'Today';
  } else if (dateMoment.isSame(yesterday, 'day')) {
    return 'Yesterday';
  } else if (dateMoment.isAfter(moment.tz(TIMEZONE).subtract(7, 'days'))) {
    return dateMoment.fromNow();
  } else {
    return formatDate(date);
  }
};

/**
 * Check if date is in past
 */
export const isDateInPast = (date: string | Date): boolean => {
  return moment.tz(date, TIMEZONE).isBefore(moment.tz(TIMEZONE).startOf('day'));
};

/**
 * Check if date is today
 */
export const isToday = (date: string | Date): boolean => {
  return moment.tz(date, TIMEZONE).isSame(moment.tz(TIMEZONE), 'day');
};

/**
 * Get month date range from a given date (YYYY-MM-DD format)
 */
export const getMonthRangeFromDate = (date: Date): { startDate: string; endDate: string } => {
  const dateMoment = moment.tz(date, TIMEZONE);
  const year = dateMoment.year();
  const month = dateMoment.month() + 1; // 1-indexed

  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = moment.tz(startDate, 'YYYY-MM-DD', TIMEZONE).endOf('month').format('YYYY-MM-DD');

  return { startDate, endDate };
};